/**
 * hsp-ga4-events.js
 *
 * Sends the GA4 events that the Google & YouTube channel app does NOT send.
 *
 * The app already sends: page_view, view_item, search, add_to_cart, begin_checkout,
 * add_payment_info, purchase. NEVER emit any of those here — duplicating them is the
 * usual way Shopify GA4 setups corrupt revenue data.
 *
 * This file adds:
 *   list      - view_item_list, select_item, filter_applied
 *   cart      - view_cart, remove_from_cart
 *   engagement- scroll, outbound_click, file_download, form_submit
 *
 * The engagement events exist because GA4 Enhanced Measurement is enabled on the
 * stream but does not actually fire — verified 2026-08-05 by scrolling a 6,212px
 * page to 100% with no `scroll` event reaching GA4. The app-injected Google tag
 * does not run EM's automatic listeners, so we implement them here.
 *
 * Why plain gtag() and not a custom pixel: the Google tag is injected into the MAIN
 * document by Shopify's web-pixels-manager, so window.gtag shares the same client_id
 * and session as the app's own events. A sandboxed custom pixel would not.
 *
 * Everything re-queries the DOM rather than caching element references, because
 * facets.js and the cart drawer swap their contents over AJAX.
 */
(function () {
  'use strict';

  var LIST_ROOT = '[data-ga-list]';
  var CART_ITEM = '[data-ga-cart-item]';

  window.dataLayer = window.dataLayer || [];

  /* ---------------------------------------------------------------- send --- */

  var queue = [];
  var waiting = false;

  /*
   * The wait used to give up after 10s and DISCARD the queue silently. On a slow
   * connection the app's tag routinely lands later than that, so early events —
   * view_item_list above all, which fires at DOMContentLoaded — were dropped with
   * no console trace. 60s is past any realistic tag load; the cap stops an
   * unbounded queue on a page where gtag genuinely never arrives.
   */
  var MAX_QUEUE = 50;

  function waitForGtag() {
    if (waiting) return;
    waiting = true;
    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      if (typeof window.gtag === 'function') {
        clearInterval(timer);
        waiting = false;
        while (queue.length) {
          var q = queue.shift();
          window.gtag('event', q[0], q[1]);
        }
      } else if (tries > 240) { // ~60s
        clearInterval(timer);
        waiting = false;
        queue.length = 0;
      }
    }, 250);
  }

  function send(name, params) {
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, params);
    } else {
      if (queue.length < MAX_QUEUE) queue.push([name, params]);
      waitForGtag();
    }
  }

  /*
   * Shared with the other theme modules that need to report to GA4.
   *
   * hp-filters.js and hp-collection-search.js each used to `window.dataLayer.push`,
   * and there is NO GTM container on this site — so both events went nowhere. Worse,
   * line 33 above creates window.dataLayer unconditionally, so their own
   * `if (!window.dataLayer) return` guards passed and the pushes looked healthy.
   *
   * Both callers fire on user interaction only (a facet change, a debounced search
   * keystroke), which cannot happen before this deferred script has run, so a plain
   * presence check on the caller's side is sufficient.
   */
  window.HSPGA4 = { send: send };

  /* Markets serve non-USD prices; a hardcoded currency mislabels every value. */
  function currency() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  }

  function num(el, attr) {
    var v = parseFloat(el.getAttribute(attr));
    return isNaN(v) ? null : v;
  }

  function baseItem(el, index) {
    var item = {
      item_id: el.getAttribute('data-ga-id'),
      item_name: el.getAttribute('data-ga-name') || '',
      index: index
    };
    var brand = el.getAttribute('data-ga-brand');
    var price = num(el, 'data-ga-price');
    if (brand) item.item_brand = brand;
    if (price !== null) item.price = price;
    return item;
  }

  /* ------------------------------------------------- product list tracking --- */

  /*
   * A page can carry SEVERAL lists at once — a product page shows two
   * recommendation rails, the cart shows a cross-sell rail, the home page a
   * situation grid. This used to be document.querySelector, so only the first
   * list on any page was ever tracked and every other rail was invisible.
   */
  function listRoots() {
    return Array.prototype.slice.call(document.querySelectorAll(LIST_ROOT));
  }

  /*
   * An impression only counts if the shopper could actually see it.
   *
   * The predictive-search panel is PRE-RENDERED into the page — measured on a
   * product page: eight hidden list roots (desktop dropdown, mobile drawer and
   * the search dialog each carry a copy), all populated with real products
   * before anyone has typed a character. Tracking roots unconditionally would
   * therefore report a typeahead impression on every single page load.
   *
   * getClientRects() is empty for display:none and for anything inside a closed
   * <dialog>; visibility:hidden still produces rects, so it is tested separately.
   */
  function isVisible(el) {
    if (!el.getClientRects().length) return false;
    return window.getComputedStyle(el).visibility !== 'hidden';
  }

  function listContext(root) {
    return {
      name: root.getAttribute('data-ga-list') || '',
      id: root.getAttribute('data-ga-list-id') || '',
      pageType: root.getAttribute('data-ga-page-type') || ''
    };
  }

  function listCards(root) {
    return Array.prototype.slice.call(root.querySelectorAll('[data-ga-id]'));
  }

  function toListItem(el, index, ctx) {
    var item = baseItem(el, index);
    if (ctx.name) item.item_list_name = ctx.name;
    if (ctx.id) item.item_list_id = ctx.id;
    return item;
  }

  /* Per-list, not global: two rails resolving at different times must not
     suppress each other's view_item_list. */
  var seenLists = {};

  function sendViewItemList() {
    listRoots().forEach(function (root, rootIndex) {
      if (!isVisible(root)) return;

      var els = listCards(root);
      if (!els.length) return; // recommendation rails render skeletons first

      var ctx = listContext(root);
      var items = els.map(function (el, i) { return toListItem(el, i, ctx); });

      var key = ctx.id || ('list' + rootIndex);
      var signature = items.map(function (i) { return i.item_id; }).join(',');
      if (seenLists[key] === signature) return;
      seenLists[key] = signature;

      send('view_item_list', {
        item_list_name: ctx.name,
        item_list_id: ctx.id,
        page_type: ctx.pageType,
        items: items.slice(0, 100) // GA4 caps items per event
      });
    });
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var card = target.closest('[data-ga-id]');
    if (!card) return;
    if (card.hasAttribute('data-ga-cart-item')) return; // cart rows are not a list

    /*
     * select_item means "chose this product and went to it". The card also wraps
     * Quick View and Add to Cart, and a capture-all listener counted those too —
     * so add-to-cart inflated select_item without anyone navigating anywhere.
     * Require an actual product link, and bail on any control.
     */
    if (target.closest('button, input, select, textarea, [role="button"]')) return;
    var link = target.closest('a[href]');
    if (!link || link.getAttribute('href').indexOf('/products/') === -1) return;

    var root = card.closest(LIST_ROOT);
    if (!root) return;

    var ctx = listContext(root);
    var index = listCards(root).indexOf(card);

    send('select_item', {
      item_list_name: ctx.name,
      item_list_id: ctx.id,
      page_type: ctx.pageType,
      items: [toListItem(card, index < 0 ? 0 : index, ctx)]
    });
  });

  /* Facet inputs render as name="{{ value.param_name }}" -> filter.p.vendor etc. */
  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input || !input.name || input.name.indexOf('filter.') !== 0) return;
    if (!input.value) return;

    /*
     * Must be the MAIN grid, not merely the first root in the document. The
     * predictive-search dropdown is a list root too and sits in the header, so
     * document order hands back the typeahead whenever it happens to be open —
     * mislabelling page_type on every facet event.
     */
    var root = document.querySelector(
      '[data-ga-list][data-ga-page-type="collection"], [data-ga-list][data-ga-page-type="search"]'
    );

    /*
     * Un-checking used to be discarded, so a facet nobody could stand looked
     * identical to one nobody touched. hp-filters.js separately reported this as
     * `filter_change` into the dead dataLayer; that has been removed and its one
     * useful extra — add vs remove — folded in here, so there is a single path.
     */
    send('filter_applied', {
      filter_type: input.name.replace(/^filter\./, ''),
      filter_value: String(input.value),
      filter_action: (input.type === 'checkbox' && !input.checked) ? 'remove' : 'add',
      page_type: root ? listContext(root).pageType : ''
    });
  });

  /* ------------------------------------------------------------ cart events --- */

  function cartItems() {
    return Array.prototype.slice.call(document.querySelectorAll(CART_ITEM));
  }

  function toCartItem(el, index) {
    var item = baseItem(el, index);
    var qty = num(el, 'data-ga-qty');
    var variant = el.getAttribute('data-ga-variant');
    if (qty !== null) item.quantity = qty;
    if (variant) item.item_variant = variant;
    return item;
  }

  function cartValue(items) {
    return items.reduce(function (sum, i) {
      return sum + ((i.price || 0) * (i.quantity || 1));
    }, 0);
  }

  var lastCartSignature = '';

  function sendViewCart() {
    var els = cartItems();
    if (!els.length) return;

    var items = els.map(toCartItem);
    var signature = items.map(function (i) { return i.item_id + 'x' + i.quantity; }).join(',');
    if (signature === lastCartSignature) return;
    lastCartSignature = signature;

    send('view_cart', {
      currency: currency(),
      value: Math.round(cartValue(items) * 100) / 100,
      items: items
    });
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var remove = target.closest('.cart-items__remove, [data-ga-cart-remove]');
    if (!remove) return;

    var row = remove.closest(CART_ITEM);
    if (!row) return;

    var item = toCartItem(row, 0);
    send('remove_from_cart', {
      currency: currency(),
      value: Math.round((item.price || 0) * (item.quantity || 1) * 100) / 100,
      items: [item]
    });
  });

  /* ------------------------------------------------------- engagement events --- */

  var DOWNLOAD_RE = /\.(pdf|docx?|xlsx?|pptx?|csv|zip|rar|7z|txt|mp3|mp4|mov|avi|dmg|exe|pkg)(\?|#|$)/i;
  var scrollSent = false;

  function onScroll() {
    if (scrollSent) return;
    var de = document.documentElement;
    var h = de.scrollHeight;
    if (h <= window.innerHeight) return;
    var reached = (window.scrollY + window.innerHeight) / h;
    if (reached >= 0.9) {
      scrollSent = true;
      send('scroll', { percent_scrolled: 90 });
      window.removeEventListener('scroll', onScroll);
    }
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var link = target.closest('a[href]');
    if (!link) return;

    var href = link.getAttribute('href') || '';
    if (!href || href.charAt(0) === '#') return;

    var url;
    try { url = new URL(link.href, window.location.href); } catch (e) { return; }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return;

    if (DOWNLOAD_RE.test(url.pathname)) {
      send('file_download', {
        link_url: url.href,
        link_text: (link.textContent || '').trim().slice(0, 100),
        file_extension: (url.pathname.split('.').pop() || '').toLowerCase()
      });
      return;
    }

    if (url.host && url.host !== window.location.host) {
      send('outbound_click', {
        link_url: url.href,
        link_domain: url.host,
        link_text: (link.textContent || '').trim().slice(0, 100)
      });
    }
  });

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || form.tagName !== 'FORM') return;

    /*
     * This listener is capture-phase over every form, which counted each AJAX
     * add-to-cart, facet change and search as a "form submit" — burying the
     * contact and newsletter submissions that are the only ones worth a lead
     * metric. Facets and search are GET; real lead forms are POST. Cart forms
     * are POST too, so exclude those by action.
     */
    var method = (form.getAttribute('method') || 'get').toLowerCase();
    if (method !== 'post') return;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart') !== -1) return;

    send('form_submit', {
      form_id: form.getAttribute('id') || '',
      form_name: form.getAttribute('name') || '',
      form_destination: form.getAttribute('action') || ''
    });
  }, true);

  /* ---------------------------------------------------------------- init ---- */

  function init() {
    /*
     * Product lists (collection, search, recommendation rails, home grid).
     * The observer is installed unconditionally now, not only when a root is
     * already present: recommendation rails render skeleton cards first and
     * assets/product-recommendations.js replaces the inner tree when the fetch
     * resolves, so at DOMContentLoaded a rail has a root but no [data-ga-id].
     */
    sendViewItemList();
    if (window.MutationObserver) {
      var listDebounce = null;
      new MutationObserver(function () {
        clearTimeout(listDebounce);
        listDebounce = setTimeout(sendViewItemList, 500);
      }).observe(document.body, {
        childList: true,
        subtree: true,
        /* Opening the search dialog reveals an already-populated list with no
           childList change, so watch the attributes that reveal it. Deliberately
           NOT class/style — those churn on every hover and would keep resetting
           the debounce. */
        attributes: true,
        attributeFilter: ['open', 'hidden', 'aria-hidden']
      });
    }

    /*
     * Cart: on the cart page immediately, and whenever the drawer reveals items.
     * Markets prefix the path (/en-ca/cart), which a plain indexOf === 0 missed,
     * so view_cart never fired for any localised visitor.
     */
    if (/^(\/[a-z]{2}(-[a-z]{2})?)?\/cart/i.test(window.location.pathname)) {
      sendViewCart();
    }
    if (window.MutationObserver) {
      var cartDebounce = null;
      new MutationObserver(function () {
        var drawer = document.querySelector('cart-drawer-component dialog[open], dialog[open] ' + CART_ITEM);
        if (!drawer) return;
        clearTimeout(cartDebounce);
        cartDebounce = setTimeout(sendViewCart, 400);
      }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['open'] });
    }

    /*
     * A search that came back empty. The marker is rendered by
     * sections/search-results.liquid only when a query was actually performed,
     * so this cannot fire on a bare /search landing.
     *
     * page_type separates this from the dropdown's own search_no_results
     * (sent by assets/predictive-search.js) and is an already-registered custom
     * dimension, so it needs no new GA4 config.
     */
    var emptySearch = document.querySelector('[data-hsp-search-empty]');
    if (emptySearch) {
      send('search_no_results', {
        search_term: emptySearch.getAttribute('data-hsp-term') || '',
        page_type: 'search'
      });
    }

    // Engagement
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
